/**
 * @file chat-canvas.tsx
 * @description Provides the main resizable layout container for canvas views.
 * It handles the UI for resizing, full-screen toggling, and displaying dynamic content.
 */

import { ArrowLeftToLine, Maximize2, Minimize2, X } from 'lucide-react';
import type { CSSProperties } from 'react';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils.js';
import { useResizable } from './use-resizable.js';

const CANVAS_CONTENT_PADDING = 'px-2.5 py-2.5 sm:px-3 sm:py-3';

interface ChatCanvasProps {
  /** The title displayed in the header of the canvas. */
  title: React.ReactNode;
  /** Callback function triggered when the close (X) button is clicked. */
  onClose: () => void;
  /** The dynamic content to be rendered inside the canvas. */
  children: React.ReactNode;
  /** The initial width of the canvas in pixels. Defaults to 500. */
  initialWidth?: number;
  /** The minimum width in pixels while dragging to resize. Defaults to 300. */
  minWidth?: number;
  /** Optional callback to navigate to the summary view, renders a back button next to the title. */
  onOpenSummary?: () => void;
}

/**
 * A resizable and collapsible side-panel component.
 */
export function ChatCanvas({ title, onClose, children, initialWidth = 500, minWidth, onOpenSummary }: ChatCanvasProps) {
  const { t } = useTranslation();
  const [isFullPage, setIsFullPage] = useState(false);
  const { width, startResizing } = useResizable(initialWidth, isFullPage, minWidth);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasStyle = {
    '--canvas-width': `${width}px`,
  } as CSSProperties & { '--canvas-width': string };

  return (
    <>
      {!isFullPage && <div className="fixed inset-0 z-40 bg-slate-950/25 md:hidden" onClick={onClose} aria-hidden />}

      <div
        ref={containerRef}
        style={canvasStyle}
        className={cn(
          'flex flex-col bg-white dark:bg-card shadow-2xl z-50 shrink-0 overflow-hidden border border-slate-100/80 dark:border-slate-700/80',
          isFullPage
            ? 'fixed inset-0 max-h-none rounded-none md:absolute md:w-full md:max-w-full md:h-full md:shadow-2xl md:fade-in-0'
            : 'fixed inset-x-0 bottom-0 w-full max-h-[92dvh] rounded-t-3xl animate-in slide-in-from-bottom-8 md:relative md:bottom-auto md:inset-auto md:h-full md:max-h-none md:w-[var(--canvas-width)] md:max-w-[calc(100%-300px)] md:rounded-none md:border-l md:border-t-0 md:shadow-xl md:slide-in-from-right-8',
        )}
      >
        <div className="mx-auto mt-2 h-1.5 w-12 rounded-full bg-slate-300 md:hidden" aria-hidden />

        {!isFullPage && (
          <div
            onMouseDown={startResizing}
            role="separator"
            aria-orientation="vertical"
            className="absolute top-0 bottom-0 left-0 z-20 hidden w-1.5 cursor-ew-resize transition-colors hover:bg-brand-500/50 active:bg-brand-500 md:block"
            title={t('Canvas.common.resize')}
          />
        )}

        <div className="sticky top-0 z-10 flex h-14 shrink-0 items-center justify-between border-b border-slate-100 dark:border-slate-700 bg-white/95 dark:bg-card/95 px-3 pl-4 backdrop-blur sm:px-4 sm:pl-6">
          <h2 className="@container min-w-0 flex-1 truncate font-semibold text-slate-700 dark:text-slate-300">
            {title}
          </h2>
          <div className="flex items-center gap-2 shrink-0">
            {onOpenSummary && (
              <button
                onClick={onOpenSummary}
                className="flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 px-2.5 py-1 text-xs font-medium text-slate-600 dark:text-slate-400 transition-colors hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-300"
                title={t('Canvas.common.toSummaryTitle')}
              >
                <ArrowLeftToLine className="h-4 w-4" />
                <span>
                  {/* {t('Canvas.common.backToSummary1')}
                  <br /> */}
                  {t('Canvas.common.backToSummary2')}
                </span>
              </button>
            )}
            <button
              onClick={() => setIsFullPage(!isFullPage)}
              className="rounded-lg p-2 transition-colors hover:bg-slate-100 dark:hover:bg-slate-700"
              title={isFullPage ? t('Canvas.common.restore') : t('Canvas.common.fullscreen')}
            >
              {isFullPage ? (
                <Minimize2 className="h-4 w-4 text-slate-600 dark:text-slate-400" />
              ) : (
                <Maximize2 className="h-4 w-4 text-slate-600 dark:text-slate-400" />
              )}
            </button>
            <button
              onClick={onClose}
              className="rounded-lg p-2 transition-colors hover:bg-slate-100 dark:hover:bg-slate-700"
              title={t('Canvas.common.close')}
            >
              <X className="h-4 w-4 text-slate-600 dark:text-slate-400" />
            </button>
          </div>
        </div>

        <div className="relative flex-1 overflow-y-auto overscroll-contain">
          <div className={cn('h-full', CANVAS_CONTENT_PADDING)}>{children}</div>
        </div>
      </div>
    </>
  );
}
